// import dayjs from 'dayjs'
import {
  Box,
  Card,
  // Chip,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material'
import {
  AssignmentIcon,
  // CalendarIcon,
  CorrectIcon,
  UserIcon,
} from 'src/v3/core/icons'
import {
  ComplianceProgress,
  ComplianceRequirementDetail,
} from 'src/v3/core/types'

type Props = {
  detail: ComplianceRequirementDetail | null
  progress: ComplianceProgress | null
  isLoading: boolean
}

function Skeleton_() {
  return (
    <Card className="p-6 mb-6">
      <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Box className="flex flex-col gap-4">
          <Skeleton variant="text" width={160} height={28} />
          <Box className="flex flex-col gap-3">
            {/* Was [100, 120, 100] for the status and date rows, now hidden. */}
            {[100, 120].map((w, i) => (
              <Box key={i} className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={w} height={20} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <Skeleton variant="text" width={140} height={28} className="mb-2" />
          <Box className="text-center md:pt-6">
            <Skeleton
              variant="text"
              width={80}
              height={60}
              className="mx-auto mb-1"
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={12}
              sx={{ borderRadius: 6 }}
              className="mb-2"
            />
            <Skeleton
              variant="text"
              width={120}
              height={20}
              className="mx-auto"
            />
          </Box>
        </Box>
        <Box className="flex flex-col gap-4">
          <Skeleton variant="text" width={120} height={28} />
          <Box className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={72}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

export default function ComplianceDetailsCard({
  detail,
  progress,
  isLoading,
}: Props) {
  if (isLoading || !detail) return <Skeleton_ />

  let completionRate = progress
    ? progress.totalUsers > 0
      ? Math.round((progress.completedUsers / progress.totalUsers) * 100)
      : 0
    : null

  // Expired/Active status is derived from the requirement's expiration date, which is
  // hidden while requirement dates are phased out.
  // let isExpired = detail.expirationDate
  //   ? dayjs(detail.expirationDate).isBefore(dayjs())
  //   : false

  // let statusLabel = isExpired ? 'Expired' : 'Active'
  // let statusColor: 'error' | 'success' = isExpired ? 'error' : 'success'

  let progressColor: 'primary' | 'success' | 'error' =
    completionRate === null
      ? 'primary'
      : completionRate === 100
        ? 'success'
        : // : isExpired
          //   ? 'error'
          'primary'

  return (
    <Card className="p-6 mb-6">
      <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Left: details */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600 }}
            className="mb-4"
          >
            Requirement Info
          </Typography>
          <Box className="flex flex-col gap-3">
            {/* Status and dates are hidden while requirement dates are phased out.
            <Box className="flex items-center gap-2">
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip label={statusLabel} color={statusColor} size="small" />
            </Box>
            <Box className="flex items-center gap-2">
              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Issue Date:
              </Typography>
              <Typography variant="body2">
                {detail.issueDate
                  ? dayjs(detail.issueDate).format('MMM D, YYYY')
                  : '—'}
              </Typography>
            </Box>
            <Box className="flex items-center gap-2">
              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Expiration:
              </Typography>
              <Typography variant="body2">
                {detail.expirationDate
                  ? dayjs(detail.expirationDate).format('MMM D, YYYY')
                  : '—'}
              </Typography>
            </Box>
            */}
            <Box className="flex items-center gap-2">
              <UserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Users:
              </Typography>
              <Typography variant="body2">{detail.userIDs.length}</Typography>
            </Box>
            <Box className="flex items-center gap-2">
              <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Requirements:
              </Typography>
              <Typography variant="body2">
                {detail.licenseItems.length +
                  detail.credentialItems.length +
                  detail.nremtItems.length +
                  detail.recertItems.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Center: completion rate */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600 }}
            className="mb-2"
          >
            Overall Completion
          </Typography>
          {progress ? (
            <Box className="text-center md:pt-4">
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: `${progressColor}.main` }}
              >
                {completionRate}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionRate ?? 0}
                color={progressColor}
                sx={{ height: 12, borderRadius: 6, my: 1.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                {progress.completedUsers} of {progress.totalUsers} users
                complete
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" className="mt-4">
              No progress data yet
            </Typography>
          )}
        </Box>

        {/* Right: user breakdown */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600 }}
            className="mb-4"
          >
            User Breakdown
          </Typography>
          {progress ? (
            <Box className="grid grid-cols-3 gap-3">
              {[
                {
                  label: 'Complete',
                  value: progress.completedUsers,
                  color: 'success.main',
                },
                {
                  label: 'In Progress',
                  value: progress.inProgressUsers,
                  color: 'warning.main',
                },
                {
                  label: 'Not Started',
                  value: progress.notStartedUsers,
                  color: 'text.secondary',
                },
              ].map(({ label, value, color }) => (
                <Box
                  key={label}
                  sx={{
                    bgcolor: 'background.paperDark',
                    borderRadius: 1,
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color }}>
                    {value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="flex items-center gap-2 mt-4">
              <CorrectIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {detail.userIDs.length} user
                {detail.userIDs.length !== 1 ? 's' : ''} assigned
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  )
}
